import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import type { DietType, SupplementType, FrequencyType } from "./options";

// ============================================================================
// Types
// ============================================================================

export interface DietDetail {
  id?: string;
  diet: DietType;
  other?: string | null;
}

export interface SupplementDetail {
  id?: string;
  supplement: SupplementType;
  other?: string | null;
  startDate?: string | null;
  frequency?: FrequencyType | null;
  notes?: string | null;
}

export interface DietSupplementsState {
  lifestyleId?: string;
  // Diet
  hasDiets: boolean | null;
  diets: DietDetail[];
  dietIdsToDelete: string[];
  // Supplements
  hasDietSupplements: boolean | null;
  supplements: SupplementDetail[];
  supplementIdsToDelete: string[];
}

interface DietSupplementsContextType {
  state: DietSupplementsState;
  updateState: (updates: Partial<DietSupplementsState>) => void;
}

// ============================================================================
// Context
// ============================================================================

const DietSupplementsContext = createContext<
  DietSupplementsContextType | undefined
>(undefined);

const initialState: DietSupplementsState = {
  lifestyleId: undefined,
  hasDiets: null,
  diets: [],
  dietIdsToDelete: [],
  hasDietSupplements: null,
  supplements: [],
  supplementIdsToDelete: [],
};

export function DietSupplementsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { data: patient, isLoading } = usePatient();
  const [state, setState] = useState<DietSupplementsState>(initialState);

  useEffect(() => {
    if (isLoading || !patient) return;

    setState((prev) => {
      const isInitialState =
        prev.hasDiets === null &&
        prev.diets.length === 0 &&
        prev.hasDietSupplements === null &&
        prev.supplements.length === 0;

      if (!isInitialState) return prev;

      const lifestyle = (patient as any)?.lifestyle;
      if (!lifestyle) return prev;

      const next: Partial<DietSupplementsState> = {};

      if (lifestyle.id) {
        next.lifestyleId = lifestyle.id;
      }

      // Load diets
      if (lifestyle.hasDiets !== undefined && lifestyle.hasDiets !== null) {
        next.hasDiets = lifestyle.hasDiets;
      }

      if (
        lifestyle.diets &&
        Array.isArray(lifestyle.diets) &&
        lifestyle.diets.length > 0
      ) {
        next.diets = lifestyle.diets.map((d: any) => ({
          id: d.id,
          diet: d.diet,
          other: d.other || null,
        }));
        // If we have diets, hasDiets is implicitly true
        if (next.hasDiets === undefined) next.hasDiets = true;
      }

      // Load supplements
      if (
        lifestyle.hasDietSupplements !== undefined &&
        lifestyle.hasDietSupplements !== null
      ) {
        next.hasDietSupplements = lifestyle.hasDietSupplements;
      }

      if (
        lifestyle.dietSupplements &&
        Array.isArray(lifestyle.dietSupplements) &&
        lifestyle.dietSupplements.length > 0
      ) {
        next.supplements = lifestyle.dietSupplements.map((s: any) => ({
          id: s.id,
          supplement: s.supplement,
          other: s.other || null,
          startDate: s.startDate || null,
          frequency: s.frequency || null,
          notes: s.notes || null,
        }));
        if (next.hasDietSupplements === undefined) {
          next.hasDietSupplements = true;
        }
      }

      if (Object.keys(next).length === 0) return prev;
      return { ...prev, ...next };
    });
  }, [patient, isLoading]);

  const updateState = useCallback((updates: Partial<DietSupplementsState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  return (
    <DietSupplementsContext.Provider value={{ state, updateState }}>
      {children}
    </DietSupplementsContext.Provider>
  );
}

export function useDietSupplements() {
  const context = useContext(DietSupplementsContext);
  if (!context) {
    throw new Error(
      "useDietSupplements must be used within DietSupplementsProvider",
    );
  }
  return context;
}
